import { baseUrl_SPB } from '../BaseUrl';

function authHeaders(token) {
    return {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
    };
}

async function parseJson(response, fallbackMessage) {
    if (!response.ok) {
        throw new Error(fallbackMessage);
    }
    return response.json();
}

async function fetchOnboardingHub(token, companyId) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/hub?companyId=${encodeURIComponent(companyId)}`, {
        headers: authHeaders(token),
    });
    return parseJson(response, 'Failed to load onboarding hub');
}

async function fetchOnboardingStatus(token, employeeId) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/status/${employeeId}`, {
        headers: authHeaders(token),
    });

    if (response.status === 404) {
        return null;
    }

    return parseJson(response, 'Failed to load onboarding status');
}

async function fetchEmployeeWorkPreferences(token, employeeId) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/employee/workpreferences?staffId=${employeeId}`, {
        headers: {
            'Authorization': 'Bearer ' + token,
        },
    });

    if (!response.ok) {
        return null;
    }

    return response.json();
}

async function startOnboarding(token, payload) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/start`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    });
    return parseJson(response, 'Failed to start onboarding');
}

async function updateOnboardingStatus(token, employeeId, payload) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/status/${employeeId}`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    });
    return parseJson(response, 'Failed to update onboarding status');
}

async function updateChecklistStep(token, stepId, payload) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/checklist/${stepId}`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    });
    return parseJson(response, 'Failed to update checklist step');
}

async function requestPresignedUrl(token, payload) {
    const firstImageKeyName = payload?.documentImages?.[0]?.keyName
        || payload?.UploadedDocuments?.[0]?.documentImages?.[0]?.keyName
        || payload?.uploadedDocuments?.[0]?.documentImages?.[0]?.keyName;

    const requestPayload = {
        ...payload,
        documentName: firstImageKeyName || payload?.documentName,
    };

    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/documents/presigned-url`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(requestPayload),
    });
    return parseJson(response, 'Failed to get upload URL');
}

async function confirmDocumentUpload(token, payload) {
    const hasFiles = Array.isArray(payload?.files) && payload.files.length > 0;

    if (hasFiles) {
        const formData = new FormData();

        const appendIfPresent = (key, value) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        };

        appendIfPresent('employeeId', payload.employeeId);
        appendIfPresent('companyId', payload.companyId);
        appendIfPresent('documentType', payload.documentType);
        appendIfPresent('documentName', payload.documentName);
        appendIfPresent('description', payload.description);
        appendIfPresent('s3Key', payload.s3Key);
        appendIfPresent('s3Bucket', payload.s3Bucket);
        appendIfPresent('contentType', payload.contentType);
        appendIfPresent('fileSize', payload.fileSize);
        appendIfPresent('required', payload.required);
        appendIfPresent('expiryDate', payload.expiryDate);

        payload.files.forEach((file) => {
            if (file) {
                formData.append('files', file);
            }
        });

        const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/documents/upload`, {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
            },
            body: formData,
        });

        return parseJson(response, 'Failed to confirm document upload');
    }

    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/documents/upload`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    });
    return parseJson(response, 'Failed to confirm document upload');
}

async function reviewDocument(token, documentId, payload) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/documents/${documentId}/review`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(payload),
    });
    return parseJson(response, 'Failed to review document');
}

async function markWelcomeSeen(token, employeeId) {
    const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/${employeeId}/welcome-seen`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify({ welcomeMessageSeen: true }),
    });
    return parseJson(response, 'Failed to update welcome state');
}

export {
    fetchOnboardingHub,
    fetchOnboardingStatus,
    fetchEmployeeWorkPreferences,
    startOnboarding,
    updateOnboardingStatus,
    updateChecklistStep,
    requestPresignedUrl,
    confirmDocumentUpload,
    reviewDocument,
    markWelcomeSeen,
};
